import {
  evaluateEmailSendBlock,
  type EmailSendBlockIntegration,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/block-rule";
import type { IntegrationWithChannelDataResponse } from "@redotech/effect-api-shared/models/shopper-ai/integration/integration";
import type { BlockContext, BlockRule, BlockedResult } from "../types";

type EmailChannel = "gmail" | "outlook";
type EmailIntegration = Extract<
  IntegrationWithChannelDataResponse,
  { integrationType: EmailChannel }
>;

function isEmailChannel(channel: string): channel is EmailChannel {
  return channel === "gmail" || channel === "outlook";
}

function findThreadEmailIntegration(
  ctx: BlockContext,
): EmailSendBlockIntegration | null | undefined {
  if (!ctx.thread || !ctx.integrations) {
    return undefined;
  }
  const threadIntegrationId = ctx.thread.integrationId;
  return (
    ctx.integrations.find(
      (i): i is EmailIntegration =>
        (i.integrationType === "gmail" || i.integrationType === "outlook") &&
        i.id === threadIntegrationId,
    ) ?? null
  );
}

function isThreadEmailIntegrationRevoked(
  ctx: BlockContext,
): EmailIntegration | undefined {
  if (!ctx.thread || !ctx.integrations) {
    return undefined;
  }
  const threadIntegrationId = ctx.thread.integrationId;
  return ctx.integrations.find(
    (i): i is EmailIntegration =>
      (i.integrationType === "gmail" || i.integrationType === "outlook") &&
      i.id === threadIntegrationId &&
      i.permissionsRevoked,
  );
}

/**
 * Block sending when the gmail/outlook integration this thread was received on
 * is no longer connected to the merchant (deleted or never existed for the
 * logged-in team). Mirrors v1's "Email integration not found" case.
 */
export const emailIntegrationMissingRule: BlockRule = (
  ctx: BlockContext,
): BlockedResult | null => {
  if (!isEmailChannel(ctx.channel)) {
    return null;
  }
  return evaluateEmailSendBlock({
    channel: ctx.channel,
    integration: findThreadEmailIntegration(ctx),
  });
};

/**
 * Block sending when the email provider has revoked Redo's permissions.
 * Mirrors v1's "permissionsRevoked" check in `useEmailIntegrationStatus`.
 */
export const emailIntegrationRevokedRule: BlockRule = (
  ctx: BlockContext,
): BlockedResult | null => {
  if (!isEmailChannel(ctx.channel)) {
    return null;
  }
  const integration = isThreadEmailIntegrationRevoked(ctx);
  if (!integration) {
    return null;
  }
  return evaluateEmailSendBlock({ channel: ctx.channel, integration });
};
